import { ref, onUnmounted } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { syncService } from '@/services/syncService.js'
import { publishSyncCompleted } from './useSyncRefresh.js'

/**
 * Composable for handling sync operations with progress tracking
 * @param {string} syncType - Type of sync: 'employees', 'prospects', 'hours'
 * @param {Function} syncFn - The actual sync function to call
 * @param {Object} options - Additional options
 * @returns {Object} Sync state and control functions
 */
export function useSyncWithProgress(syncType, syncFn, options = {}) {
  const {
    pollInterval = 500, // Poll every 500ms for responsive updates
    onSuccess = null,
    onError = null
  } = options

  const queryClient = useQueryClient()

  // State
  const isModalOpen = ref(false)
  const isSyncing = ref(false)
  const progress = ref({
    active: false,
    step: null,
    processed: 0,
    total: 0,
    message: null
  })
  const hasError = ref(false)
  const errorMessage = ref(null)
  const syncResult = ref(null)

  // Poll interval and timeout references
  let pollIntervalId = null
  let initialPollTimeoutId = null

  // Generation token: bumped on every start/stop so a poll response that was
  // already in flight when polling stopped can never overwrite newer state
  // (e.g. flip `active` back to true after completion, locking the modal)
  let generation = 0

  // Guards against acting on a stale 'done'/'error' left over from a previous
  // sync before the current run has been observed as active
  let sawActiveThisRun = false

  /**
   * Poll the backend for sync progress
   */
  const pollProgress = async () => {
    const gen = generation
    try {
      const data = await syncService.getProgress(syncType)

      // Response belongs to a stopped or older polling round: discard it
      if (gen !== generation) return

      if (data.active) {
        sawActiveThisRun = true
      }

      // A terminal step before this run was ever seen active is leftover state
      // from a previous sync — ignore it and keep polling
      if ((data.step === 'done' || data.step === 'error') && !sawActiveThisRun) {
        return
      }

      // Only update if we got valid data and sync is still active
      if (data.active || data.step) {
        progress.value = {
          active: data.active,
          step: data.step,
          processed: data.processed || 0,
          total: data.total || 0,
          message: data.message
        }
      }

      // Only stop polling if backend says sync is done (not just inactive)
      if (data.step === 'done' || data.step === 'error') {
        stopPolling()
      }
    } catch (_error) {
      // Don't stop polling on error - might be temporary
    }
  }

  /**
   * Start polling for progress
   */
  const startPolling = () => {
    if (pollIntervalId) return

    // Set up interval - first poll after small delay to let backend start
    pollIntervalId = setInterval(pollProgress, pollInterval)

    // Initial poll after 100ms to give backend time to set up
    // Store the timeout ID so we can clear it if stopPolling is called early
    initialPollTimeoutId = setTimeout(() => {
      initialPollTimeoutId = null
      pollProgress()
    }, 100)
  }

  /**
   * Stop polling for progress
   */
  const stopPolling = () => {
    // Invalidate any in-flight poll responses
    generation++

    if (pollIntervalId) {
      clearInterval(pollIntervalId)
      pollIntervalId = null
    }
    // Also clear the initial poll timeout to prevent orphaned callbacks
    if (initialPollTimeoutId) {
      clearTimeout(initialPollTimeoutId)
      initialPollTimeoutId = null
    }
  }

  /**
   * Start the sync operation
   */
  const startSync = async () => {
    // Reset state for a new polling round
    generation++
    sawActiveThisRun = false
    hasError.value = false
    errorMessage.value = null
    syncResult.value = null
    isSyncing.value = true

    // Set initial progress
    progress.value = {
      active: true,
      step: 'starting',
      processed: 0,
      total: 0,
      message: 'Synchronisatie starten...'
    }

    // Open modal
    isModalOpen.value = true

    // Start polling before calling sync
    startPolling()

    try {
      // Call the actual sync function
      const result = await syncFn()

      // Check if it was rejected due to concurrent sync
      if (result.success === false && result.error) {
        throw new Error(result.error)
      }

      // Notify this tab immediately that the synchronized data is fresh. Other
      // clients observe the updated last-synced timestamp through the global
      // listener; the local marker prevents this tab from processing the same
      // completion again during polling.
      publishSyncCompleted(syncType)

      // Sync completed
      syncResult.value = result
      isSyncing.value = false
      stopPolling()

      // Invalidate last-synced timestamps so all views update
      queryClient.invalidateQueries({ queryKey: ['last-synced'] })

      // Update progress to done
      progress.value = {
        active: false,
        step: 'done',
        processed: result.data?.inserted + result.data?.updated + result.data?.deleted || 0,
        total: result.data?.inserted + result.data?.updated + result.data?.deleted || 0,
        message: 'Synchronisatie voltooid'
      }

      // Call success callback
      if (onSuccess) {
        onSuccess(result)
      }

      return result
    } catch (error) {
      console.error('Sync failed:', error)

      // IMPORTANT: Stop polling FIRST to prevent it from overwriting error state
      stopPolling()

      // Set error state
      hasError.value = true
      isSyncing.value = false

      // Check for 409 Conflict (concurrent sync)
      if (error.response?.status === 409) {
        const activeSync = error.response.data.activeSync
        if (activeSync) {
          const names = {
            employees: 'Medewerkers',
            'employees-all': 'Volledige medewerkers',
            hours: 'Uren',
            prospects: 'Projecten',
            'prospects-historical': 'Historische saleskansen',
            'ml-training': 'ML model training',
            projects: 'Projecten',
            'timeline-prospects': 'Timeline verrijking',
            services: 'Diensten & facturen',
            all: 'Volledige'
          }
          const name = names[activeSync.sync_type] || activeSync.sync_type
          const ageMs = Date.now() - new Date(activeSync.started_at).getTime()
          const minutes = Math.round(ageMs / 60000)
          errorMessage.value = `Er draait al een '${name}' synchronisatie (${minutes} min bezig). Ontgrendel via Admin > Synchronisatie.`
        } else {
          errorMessage.value = error.response.data.error || 'Er draait al een synchronisatie'
        }
      } else {
        errorMessage.value = error.message || 'Onbekende fout opgetreden'
      }

      // Set progress to error state - this must happen AFTER stopping polling
      progress.value = {
        active: false,
        step: 'error',
        processed: 0,
        total: 0,
        message: errorMessage.value
      }

      // Call error callback
      if (onError) {
        onError(error)
      }

      // Don't re-throw the error - let the modal handle it
      // throw error
    }
  }

  /**
   * Retry the sync operation
   */
  const retrySync = () => {
    startSync()
  }

  /**
   * Close the modal
   */
  const closeModal = () => {
    // Allow closing unless a sync is genuinely running. isSyncing is owned by
    // startSync itself, so a late poll response can never re-lock the modal.
    if (!isSyncing.value || hasError.value) {
      isModalOpen.value = false
      stopPolling()
    }
  }

  /**
   * Reset all state
   */
  const reset = () => {
    stopPolling()
    isModalOpen.value = false
    isSyncing.value = false
    hasError.value = false
    errorMessage.value = null
    syncResult.value = null
    progress.value = {
      active: false,
      step: null,
      processed: 0,
      total: 0,
      message: null
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopPolling()
  })

  return {
    // State
    isModalOpen,
    isSyncing,
    progress,
    hasError,
    errorMessage,
    syncResult,

    // Methods
    startSync,
    retrySync,
    closeModal,
    reset
  }
}

export default useSyncWithProgress
